import { pt, type Pt } from './geom';
import { fillPoly, solid, type Prim } from './spec';

/**
 * **红楔子。**
 *
 * Lissitzky《用红楔子打败白人》(1919) 里那枚楔子 —— 全套视觉的原点，
 * 现在也是这个游戏自己的标记。
 *
 * ## ⚠️ 「标记」和「动作」是两件事
 *
 * 楔子从项目一开始就属于 `Exception` 白卡，而且 `PRESS.red` 写着
 * 「只有异常牌能用」。把它拿去做 Logo，那条唯一性就没了 —— 除非把话说清楚：
 *
 * | | 是什么 | 允许在哪 |
 * | --- | --- | --- |
 * | **标记**（stamp） | 小、被框住、**不出血** | Logo / favicon / 品牌位置 |
 * | **动作**（the act） | 满版、**出血**、刺穿环 | **只属于异常牌** |
 *
 * 于是红楔子是全套的图腾，而异常仍然是唯一一张**楔子变成了武器**的牌。
 * 这条分工写在 `docs/card-faces.md` §5.1，改这里之前先读那一节。
 *
 * ## 形状
 *
 * ⚠️ 必须**长而扁**，不能是等边三角形。
 * 一个朝右的等边红三角在浏览器标签栏里就是**播放键** ——
 * 长宽比 2.8 : 1、尾端是一条竖边，才不会读成播放器图标。
 * 这个比例是从原始海报上量的（`card-faces.md` §5.1 记着那张牌的楔子尺寸）。
 */

export interface WedgeOpts {
  /** 楔子**包围盒的中心** */
  readonly x: number;
  readonly y: number;
  /** 长度（设计单位） */
  readonly length?: number;
  /**
   * 尾端高度 ÷ 长度。原始海报上是 **38 : 109 ≈ 0.35**。
   * ⚠️ 调大就往"三角"靠、调小就往"针"靠，两头都会丢掉"楔子"的身份
   */
  readonly backRatio?: number;
  /** 指向。`0` = 朝右，负值 = 转向右上 */
  readonly angleDeg?: number;
  readonly color?: number;
  readonly alpha?: number;
  /** 描边。⚠️ 只有压在别的图形上时才要（白卡那枚压在黑环上，所以有） */
  readonly stroke?: { readonly width: number; readonly color: number };
}

/**
 * 楔子的多边形。
 *
 * 局部坐标：尾端竖边在 `x = -L/2`（从 `-H/2` 到 `+H/2`），尖端在 `+L/2`。
 * 所以**包围盒中心就是原点** —— 摆放的时候不用再算重心。
 */
export function wedgePoly(o: WedgeOpts): Pt[] {
  const len = o.length ?? 82;
  const h = (len * (o.backRatio ?? 0.35)) / 2;
  const a = ((o.angleDeg ?? -38) * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  const local: readonly Pt[] = [pt(-len / 2, -h), pt(len / 2, 0), pt(-len / 2, h)];
  return local.map((p) => pt(o.x + p.x * c - p.y * s, o.y + p.x * s + p.y * c));
}

/** 楔子 → IR 图元 */
export function wedgeMark(o: WedgeOpts): Prim[] {
  const poly = wedgePoly(o);
  const paint = solid(o.color ?? 0xe0261f, o.alpha ?? 1);
  return [o.stroke === undefined ? fillPoly(poly, paint) : fillPoly(poly, paint, o.stroke)];
}
